import { useSidebarStore } from "./sidebar-store";
import { useNavigationMode } from "@/modules/navigation";
import { EntitySidebar } from "./entity-sidebar";
import { GlobalRail, RAIL_WIDTH } from "./global-rail";
import { DomainSidebar } from "./domain-sidebar";
import { useHotkeys } from "react-hotkeys-hook";
import { cn } from "@/lib/utils";

/**
 * Main sidebar orchestrator.
 *
 * Layout is always: Global Rail + Secondary Sidebar
 *
 * The Global Rail (icons) is always visible for domain switching.
 * The secondary sidebar changes based on context:
 * - Browse mode: Domain sidebar (domain-specific navigation)
 * - Entity mode with useSidebarConfig: Entity sidebar (custom entity navigation)
 * - Entity mode without useSidebarConfig: Falls back to domain sidebar
 *
 * In focus mode (useFocusMode) the secondary sidebar is not part of the
 * layout; the rail's latch opens it as an overlay that closes on Escape, on
 * leaving the rail and the panel together, or on following a link.
 */
export function Sidebar() {
  const {
    config,
    focus,
    overlayOpen,
    setOverlayOpen,
    cancelOverlayClose,
    scheduleOverlayClose,
  } = useSidebarStore();
  const { currentDomain, domainSidebar } = useNavigationMode();

  useHotkeys("escape", () => setOverlayOpen(false), { enabled: focus && overlayOpen });

  const secondary = config ? (
    <EntitySidebar config={config} />
  ) : (
    <DomainSidebar config={domainSidebar} />
  );

  return (
    <div className="relative flex h-svh shrink-0">
      <GlobalRail currentDomain={currentDomain} />
      {!focus && secondary}
      {focus && (
        // The clip keeps the sliding panel from passing under the pointer on
        // the rail, which would fire mouseleave and close it mid-slide.
        // RAIL_WIDTH is the right offset because focus mode forces the rail
        // collapsed; if that ever changes, this has to follow the live width.
        <div
          className="absolute top-0 h-svh z-40 overflow-hidden pointer-events-none"
          style={{ left: RAIL_WIDTH, width: 240 }}
        >
          <div
            className={cn(
              "h-svh w-fit transition-transform duration-200 ease-out",
              overlayOpen
                ? "translate-x-0 shadow-lg pointer-events-auto"
                : "-translate-x-full",
            )}
            aria-hidden={!overlayOpen}
            onMouseEnter={cancelOverlayClose}
            onMouseLeave={scheduleOverlayClose}
            onClickCapture={(e) => {
              if ((e.target as HTMLElement).closest("a")) setOverlayOpen(false);
            }}
          >
            {secondary}
          </div>
        </div>
      )}
    </div>
  );
}
